import React, { useEffect, useRef, useState } from 'react';
import { BrickBreakerEngine } from '../game/BrickBreakerEngine';
import type { GameMode, GameSnapshot, UpgradeId, UpgradeOption } from '../game/types';
import { UpgradeModal } from './UpgradeModal';

interface GameScreenProps {
  mode: GameMode;
  onHome: () => void;
  onToast: (message: string) => void;
}

const initialSnapshot: GameSnapshot = { score: 0, level: 1, lives: 3, combo: 0, balls: 1, status: 'ready', highScore: 0 };

export function GameScreen({ mode, onHome, onToast }: GameScreenProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const engineRef = useRef<BrickBreakerEngine | null>(null);
  const [snapshot, setSnapshot] = useState<GameSnapshot>(initialSnapshot);
  const [upgradeOptions, setUpgradeOptions] = useState<UpgradeOption[] | null>(null);
  const [result, setResult] = useState<'game-over' | 'victory' | null>(null);
  const [soundOn, setSoundOn] = useState(true);

  useEffect(() => {
    if (!canvasRef.current) return;
    const engine = new BrickBreakerEngine(canvasRef.current, mode, {
      onSnapshot: setSnapshot,
      onLevelClear: (options) => setUpgradeOptions(options),
      onGameOver: () => setResult('game-over'),
      onVictory: () => setResult('victory'),
      onToast
    });
    engineRef.current = engine;
    return () => {
      engine.destroy();
      engineRef.current = null;
    };
  }, [mode, onToast]);

  const chooseUpgrade = (id: UpgradeId) => {
    setUpgradeOptions(null);
    engineRef.current?.applyUpgrade(id);
  };

  const restart = () => {
    setResult(null);
    setUpgradeOptions(null);
    engineRef.current?.restart();
  };

  const toggleSound = () => {
    const enabled = engineRef.current?.toggleSound() ?? soundOn;
    setSoundOn(enabled);
    onToast(enabled ? '音效已开启' : '音效已关闭');
  };

  return (
    <main className="game-page">
      <section className="game-panel">
        <div className="canvas-wrap">
          <canvas ref={canvasRef} className="game-canvas" aria-label="弹球砖块破坏者游戏画布" />
          {snapshot.status === 'paused' && (
            <div className="game-overlay">
              <div className="overlay-card">
                <h2>游戏已暂停</h2><p>按 P 键或点击继续按钮回到战斗。</p>
                <div className="overlay-actions"><button className="primary-btn" onClick={() => engineRef.current?.togglePause()}>继续游戏</button></div>
              </div>
            </div>
          )}
          {result && (
            <div className="game-overlay">
              <div className="overlay-card">
                <h2>{result === 'victory' ? '核心防线已突破！' : '核心能量耗尽'}</h2>
                <p>本局得分：<strong>{snapshot.score.toLocaleString()}</strong><br />最高得分：{snapshot.highScore.toLocaleString()}</p>
                <div className="overlay-actions">
                  <button className="primary-btn" onClick={restart}>再来一局</button>
                  <button className="secondary-btn" onClick={onHome}>返回游戏首页</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
      <aside className="sidebar">
        <section className="hud-card">
          <div className="hud-head">GAME STATUS · {mode === 'daily' ? '每日挑战' : '标准模式'}</div>
          <div className="hud-body">
            <div className="score-value">{snapshot.score.toLocaleString()}</div>
            <div className="hud-grid">
              <div className="metric"><span>关卡</span><strong>{snapshot.level}</strong></div>
              <div className="metric"><span>最高分</span><strong>{snapshot.highScore.toLocaleString()}</strong></div>
              <div className="metric"><span>弹球数</span><strong>{snapshot.balls}</strong></div>
              <div className="metric"><span>生命</span><strong className="lives">{'♥'.repeat(Math.max(0, snapshot.lives))}</strong></div>
            </div>
          </div>
        </section>
        <section className="hud-card">
          <div className="hud-head">实时能力</div>
          <div className="hud-body skill-list">
            <div className="skill"><span>连击倍率</span><strong>×{Math.max(1, Math.min(10, snapshot.combo))}</strong></div>
            <div className="skill"><span>特殊砖</span><small>装甲 / 爆炸 / 奖励</small></div>
            <div className="skill"><span>掉落道具</span><small>多球 / 扩板 / 减速</small></div>
          </div>
        </section>
        <section className="hud-card">
          <div className="hud-head">操作说明</div>
          <div className="hud-body control-list">
            <span>鼠标或触摸：移动挡板</span>
            <span>← → / A D：键盘移动</span>
            <span>空格 / 点击：发射弹球</span>
            <span>P：暂停　R：重新开始</span>
          </div>
        </section>
        <section className="hud-card">
          <div className="hud-head">游戏控制</div>
          <div className="hud-body game-tools">
            <button className="icon-btn" onClick={() => engineRef.current?.togglePause()}>{snapshot.status === 'paused' ? '▶ 继续' : 'Ⅱ 暂停'}</button>
            <button className="icon-btn" onClick={toggleSound}>{soundOn ? '♫ 音效开' : '♩ 音效关'}</button>
            <button className="icon-btn danger" onClick={restart}>↻ 重开</button>
            <button className="icon-btn" onClick={onHome}>⌂ 首页</button>
          </div>
        </section>
      </aside>
      {upgradeOptions && <UpgradeModal options={upgradeOptions} onChoose={chooseUpgrade} />}
    </main>
  );
}
